import React, { useEffect, useCallback, useState, useRef } from 'react'
import { Redirect, Route, useLocation } from 'react-router-dom'
import moment from 'moment'
import { ROUTES } from '../Utils/constants'

import { useDispatch, useSelector } from 'react-redux'
import { useErrorBoundary } from 'use-error-boundary'
import ErrorHandler from '../Components/ErrorHandler'
import { useLogout } from 'Hooks/useLogout'
import Loading from 'Components/Loading'
import LoginForm from 'Views/Login/Components/LoginForm'
import { Modal } from '@mui/material'
import { openLoginModal, authenticateUser } from 'redux/actions/loginActions'
import { useInactivity } from 'Views/CustomerDisplay/hook/useInactivity'
import { isCustomerDisplayRouteSupported } from 'Views/CustomerDisplay/utils/customerDisplayRouteSupport'

const AppRoutes = ({
  component: Component,
  path,
  exact,
  isPrivate,
  ...rest
}) => {
  const { logout } = useLogout()
  const dispatch = useDispatch()
  const location = useLocation()

  const { ErrorBoundary, didCatch, error } = useErrorBoundary()

  const [loading, setLoading] = useState(true)
  const loginDay = useSelector(state => state.login?.loginAt)
  const token = useSelector(state => state.login?.user?.CustomerInfo?.LoginCode)
  const { isLoginModal } = useSelector(state => state.login)
  const cargando = useSelector(state => state.login.loading)
  const errorLogin = useSelector(state => state.login.error)
  const company = useSelector(state => state.company)
  const { isActiveCustomerDisplay } = useSelector(
    state => state.customerDisplay
  )
  const [tenantValue, setTenantValue] = useState('')

  const [showPassword, setShowPassword] = useState(false)

  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')

  const channelRef = useRef(null)
  if (!channelRef.current) {
    channelRef.current = new BroadcastChannel('customer_display')
  }
  // Tracks current sleep mode to avoid stale closures inside event listeners
  const isSleepModeRef = useRef(false)

  const isCustomerDisplayRoute = location.pathname.includes(
    ROUTES.customerDisplay
  )
  const isMainRouteCustomerDisplaySupported = isCustomerDisplayRouteSupported(
    location.pathname
  )
  const shouldHandleCustomerDisplaySleep =
    !isCustomerDisplayRoute && isMainRouteCustomerDisplaySupported
  const isInactive = useInactivity(
    3 * 60 * 1000, // 3 minutes
    shouldHandleCustomerDisplaySleep ? channelRef.current : null
  )

  const toggleVisibility = () => {
    setShowPassword(!showPassword)
  }

  const checkSessionTime = useCallback(async () => {
    const day = moment().day()
    if (day !== loginDay) {
      await logout()
    }
    setLoading(false)
  }, [])

  // Automatic inactivity: notify CD and keep ref in sync
  useEffect(() => {
    if (
      isActiveCustomerDisplay &&
      isMainRouteCustomerDisplaySupported &&
      channelRef.current
    ) {
      isSleepModeRef.current = isInactive
      channelRef.current.postMessage({ isSleepMode: isInactive })
    }
  }, [isInactive, isActiveCustomerDisplay, isMainRouteCustomerDisplaySupported])

  // Wake up on any activity: main screen events OR CD userActivity.
  // Needed for manual sleep (isInactive stays false so the effect above never fires).
  // isSleepModeRef is also synced from Customer Display channel messages.
  useEffect(() => {
    const channel = channelRef.current

    const wakeUp = () => {
      if (
        isActiveCustomerDisplay &&
        isMainRouteCustomerDisplaySupported &&
        isSleepModeRef.current
      ) {
        isSleepModeRef.current = false
        channel.postMessage({ isSleepMode: false })
      }
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach(e => window.addEventListener(e, wakeUp))

    const handleChannelMessage = event => {
      // Sync ref when another Customer Display control toggles sleep mode
      if (event.data.isSleepMode !== undefined) {
        isSleepModeRef.current = event.data.isSleepMode
      }
      if (event.data.userActivity) wakeUp()
    }
    channel.addEventListener('message', handleChannelMessage)

    return () => {
      events.forEach(e => window.removeEventListener(e, wakeUp))
      channel.removeEventListener('message', handleChannelMessage)
    }
  }, [isActiveCustomerDisplay, isMainRouteCustomerDisplaySupported])

  useEffect(() => {
    loading && checkSessionTime()
    window.addEventListener('click', checkSessionTime)
    return () => {
      window.removeEventListener('click', checkSessionTime)
    }
  }, [loading])

  if (didCatch) return <ErrorHandler error={error.message} />
  if (loading) return <Loading full />

  const submitLogin = async e => {
    e.preventDefault()
    const response = await dispatch(
      authenticateUser(email, password, tenantValue, company)
    )
    if (response) {
      dispatch(openLoginModal(false))
    }
  }

  return (
    <ErrorBoundary>
      <Modal
        open={isLoginModal}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 99999,
        }}>
        <form
          onSubmit={submitLogin}
          className="login-form-expired"
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 60,
            backgroundColor: '#FFF',
            borderRadius: 5,
          }}>
          {' '}
          <h2 style={{ marginBottom: 10 }}>
            Sesión expirada, introduce tus credenciales.
          </h2>
          <LoginForm
            setTenantValue={setTenantValue}
            tenantValue={tenantValue}
            setEmail={setEmail}
            email={email}
            showPassword={showPassword}
            setPassword={setPassword}
            password={password}
            toggleVisibility={toggleVisibility}
            cargando={cargando}
            error={errorLogin}
            isLoginTokenExpired
          />
        </form>
      </Modal>

      <Route
        path={path}
        exact={exact}
        render={props => {
          if (isPrivate && token !== 200) {
            return <Redirect to={{ pathname: ROUTES.login }} />
          } else {
            if (path === ROUTES.login && token === 200) {
              return <Redirect to={{ pathname: ROUTES.home }} />
            }
            return <Component {...props} />
          }
        }}
        {...rest}
      />
    </ErrorBoundary>
  )
}

export default AppRoutes
